import { ContentfulClientApi, createClient, EntryCollection } from "contentful";

import CommonUtils from "~/utils/common-utils";
import contentfulUtils from "~/utils/contentful-utils";

import Logger from "./logger/logger";

export default class Contentful {
  private client: ContentfulClientApi;
  private locale: string | undefined;
  private include = 5 as const;

  constructor({ locale, preview = false }: { locale?: string; preview?: boolean } = {}) {
    this.locale = locale;

    if (preview) {
      this.client = createClient({
        space: process.env.CONTENTFUL_SPACE_ID!,
        accessToken: process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN!,
        environment: process.env.CONTENTFUL_ENVIRONMENT_ID!,
        host: "preview.contentful.com",
      });
    } else {
      this.client = createClient({
        space: process.env.CONTENTFUL_SPACE_ID!,
        accessToken: process.env.CONTENTFUL_ACCESS_TOKEN!,
        environment: process.env.CONTENTFUL_ENVIRONMENT_ID!,
      });
    }
  }

  // TODO: ORDER ALL THE METHODS ALPHABETICALLY TO REDUCE THE RISK OF CONFLICTS!!

  async getEditorialPageBySlug(slug: string) {
    const collection = await this.client.getEntries({
      "fields.slug": slug,
      content_type: "editorialPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getEntry(id: string) {
    try {
      return await this.client.getEntry(id, {
        locale: this.locale,
        include: this.include,
      });
    } catch (err) {
      Logger.instance.error("fetching entries:", err);
    }
  }

  async getEntriesByContentType(contentType: string, limit = 100, cleanup = false) {
    const maxLimit = 1000;
    let currentLimit = limit > maxLimit ? maxLimit : limit;
    let skip = 0;

    let collection: Omit<EntryCollection<unknown>, "stringifySafe" | "toPlainObject"> = {
      items: [],
      total: 0,
      skip: 0,
      limit: 0,
    };

    do {
      const newCollection = await this.client.getEntries({
        content_type: contentType,
        locale: this.locale,
        include: this.include,
        skip,
        limit: currentLimit,
      });

      if (CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_CONTENTFUL_JSON_CLEANUP) && cleanup) {
        newCollection.items = newCollection.items.map((item) => contentfulUtils.jsonCleanup(item));
      }

      collection.items.push(...newCollection.items);
      skip += currentLimit;
      limit = limit === Infinity ? newCollection.total : limit;
      currentLimit = Math.min(maxLimit, limit - skip);
    } while (skip < limit);

    return collection;
  }

  async getProductListingPageBySlug(slug: string) {
    const collection = await this.client.getEntries({
      content_type: "productListingPage",
      "fields.category.fields.slug": slug,
      "fields.category.sys.contentType.sys.id": "categoryBinding",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getCartPage() {
    const collection = await this.client.getEntries({
      "fields.slug": "cart",
      content_type: "cartPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getCheckoutPage() {
    const collection = await this.client.getEntries({
      "fields.slug": "checkout",
      content_type: "checkoutPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getCategoryBindingFromId(categoryId: string, overrideLocale?: string) {
    const collection = await this.client.getEntries({
      content_type: "categoryBinding",
      "fields.categoryId": categoryId,
      locale: overrideLocale ?? this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getCategoryBindingsFromIds(categoryIds: string[], overrideLocale?: string) {
    const collection = await this.client.getEntries({
      content_type: "categoryBinding",
      "fields.categoryId[in]": categoryIds.join(","),
      locale: overrideLocale ?? this.locale,
      include: this.include,
      limit: categoryIds.length,
    });

    return collection.items;
  }

  async getCategoryBindingFromSlug(slug: string) {
    const collection = await this.client.getEntries({
      content_type: "categoryBinding",
      "fields.slug": slug,
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getLoginPage() {
    const collection = await this.client.getEntries({
      "fields.slug": "login",
      content_type: "specialPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getMessagePage(slug: string | "password-reset-confirmation" | "password-reset" | "password-reset-request") {
    const collection = await this.client.getEntries({
      "fields.slug": slug,
      content_type: "specialPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getThankyouPage() {
    const collection = await this.client.getEntries({
      "fields.slug": "thankyou",
      content_type: "specialPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getProductDetailPageWithGeneric(productId: string, genericId = "generic_pdp") {
    const collection = await this.client.getEntries({
      "fields.internalName[in]": [productId, genericId].join(","),
      content_type: "productDetailPage",
      locale: this.locale,
      include: this.include,
      limit: 2,
    });

    return {
      // @ts-expect-error - Untyped Entry
      page: collection.items.find((i) => i.fields.internalName === productId),
      // @ts-expect-error - Untyped Entry
      genericPage: collection.items.find((i) => i.fields.internalName === genericId)!,
    };
  }

  async getProductDetailPageByID(name: string) {
    const collection = await this.client.getEntries({
      "fields.internalName": name,
      content_type: "productDetailPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getRequestReturnPage() {
    const collection = await this.client.getEntries({
      "fields.slug": "request-a-return",
      content_type: "specialPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getTrackYourOrderPage() {
    const collection = await this.client.getEntries({
      "fields.slug": "track-your-order",
      content_type: "specialPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getLoginSignUpPage() {
    const collection = await this.client.getEntries({
      "fields.slug": "login-sign-up",
      content_type: "specialPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getSearchPage() {
    const collection = await this.client.getEntries({
      "fields.internalName": "search",
      content_type: "searchPage",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getSlugsOfAllLocales(contentType: string, slug: string) {
    const collection = await this.client.getEntries({
      content_type: contentType,
      "fields.slug": slug,
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    if (!collection.items[0]) return null;

    const allCollection = await this.client.getEntry(collection.items[0].sys.id, {
      locale: "*",
    });

    return (allCollection?.fields as { slug: Record<string, string> }).slug || null;
  }

  async get404Page() {
    const collection = await this.client.getEntries({
      content_type: "page404",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }

  async getRedirectFile(locale: string) {
    const collection = await this.client.getAssets({
      "metadata.tags.sys.id[in]": "awsredirects",
      "fields.title[in]": "REDIRECTS " + locale.toUpperCase().replace("-", "_"),
      locale: "default",
      limit: 1,
    });

    return collection.items[0];
  }

  async getVirtualGiftCardConfig() {
    const collection = await this.client.getEntries({
      content_type: "giftCardsConfiguration",
      locale: this.locale,
      include: this.include,
      limit: 1,
    });

    return collection.items[0];
  }
}
